import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Coffee, UtensilsCrossed, Salad, IceCream, Phone, Soup, Sandwich, Drumstick, Egg, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category configuration with icons
const CATEGORY_CONFIG = {
  'All': { icon: <UtensilsCrossed className="w-4 h-4" />, order: 0 },
  'Hot Beverages': { icon: <Coffee className="w-4 h-4" />, order: 1 },
  'Cold Beverages': { icon: <IceCream className="w-4 h-4" />, order: 2 },
  'Iced Tea & Mix Drinks': { icon: <Soup className="w-4 h-4" />, order: 3 },
  'Nutri Fluids & Shakes': { icon: <Salad className="w-4 h-4" />, order: 4 },
  'Lassi': { icon: <Soup className="w-4 h-4" />, order: 5 },
  'Frozen World': { icon: <IceCream className="w-4 h-4" />, order: 6 },
  'Breakfast': { icon: <Egg className="w-4 h-4" />, order: 7 },
  'Egg Specials': { icon: <Egg className="w-4 h-4" />, order: 8 },
  'Quick Bites': { icon: <Flame className="w-4 h-4" />, order: 9 },
  'Sandwiches': { icon: <Sandwich className="w-4 h-4" />, order: 10 },
  'Wraps & Rolls': { icon: <Sandwich className="w-4 h-4" />, order: 11 },
  'Burgers': { icon: <Sandwich className="w-4 h-4" />, order: 12 },
  'Side Orders': { icon: <UtensilsCrossed className="w-4 h-4" />, order: 13 },
  'Indo-Chinese Veg': { icon: <Flame className="w-4 h-4" />, order: 14 },
  'Indo-Chinese Non-Veg': { icon: <Drumstick className="w-4 h-4" />, order: 15 },
  'Rice & Noodles': { icon: <Soup className="w-4 h-4" />, order: 16 },
  'Biryani': { icon: <Flame className="w-4 h-4" />, order: 17 },
  'Gravies': { icon: <Soup className="w-4 h-4" />, order: 18 },
};

const Menu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API}/menu`);
        setMenuItems(response.data);
      } catch (error) {
        console.error('Error fetching menu:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  // Get unique categories from menu items, sorted by config order
  const categories = useMemo(() => {
    const uniqueCats = [...new Set(menuItems.map(item => item.category))];
    const sortedCats = uniqueCats.sort((a, b) => {
      const orderA = CATEGORY_CONFIG[a]?.order ?? 99;
      const orderB = CATEGORY_CONFIG[b]?.order ?? 99;
      return orderA - orderB;
    });
    return [{ name: 'All', icon: CATEGORY_CONFIG['All'].icon }, ...sortedCats.map(cat => ({
      name: cat,
      icon: CATEGORY_CONFIG[cat]?.icon || <UtensilsCrossed className="w-4 h-4" />
    }))];
  }, [menuItems]);

  // Memoized filtered items
  const filteredItems = useMemo(() => {
    if (activeCategory === 'All') return menuItems;
    return menuItems.filter(item => item.category === activeCategory);
  }, [menuItems, activeCategory]);

  // Group items by category for "All" view
  const groupedItems = useMemo(() => {
    if (activeCategory !== 'All') return null;
    const groups = {};
    menuItems.forEach(item => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    // Sort by category order
    return Object.entries(groups).sort((a, b) => {
      const orderA = CATEGORY_CONFIG[a[0]]?.order ?? 99;
      const orderB = CATEGORY_CONFIG[b[0]]?.order ?? 99;
      return orderA - orderB;
    });
  }, [menuItems, activeCategory]);

  const handleCategoryChange = useCallback((cat) => {
    setActiveCategory(cat);
  }, []);

  return (
    <div className="page-wrapper pt-24" data-testid="menu-page">
      {/* Hero Banner */}
      <section className="relative py-16 md:py-24 px-6 md:px-12 bg-[#F0EBE0]" data-testid="menu-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-accent text-2xl text-[#E07A5F] block mb-2"
          >
            Hungry?
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-black text-[#4A3B32] mb-4"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Our Menu
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#6D5D53] max-w-xl mx-auto"
          >
            136 delicious items at student-friendly prices. All items freshly prepared with love!
          </motion.p>
        </div>
      </section>

      {/* Category Filter - Scrollable */}
      <section className="sticky top-20 z-30 bg-[#F9F7F2] border-b border-[#E6DCC8] py-4 px-4 md:px-12" data-testid="category-filter">
        <div className="max-w-7xl mx-auto">
          <div className="overflow-x-auto scrollbar-hide">
            <Tabs value={activeCategory} onValueChange={handleCategoryChange} className="w-max min-w-full">
              <TabsList className="bg-transparent flex gap-2 h-auto p-0">
                {categories.map((cat) => (
                  <TabsTrigger
                    key={cat.name}
                    value={cat.name}
                    data-testid={`category-${cat.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    className={`rounded-full px-4 py-2 font-medium whitespace-nowrap transition-all duration-200 flex items-center gap-2 ${
                      activeCategory === cat.name
                        ? 'bg-[#4A3B32] text-white'
                        : 'bg-white text-[#4A3B32] hover:bg-[#F0EBE0] border border-[#E6DCC8]'
                    }`}
                  >
                    {cat.icon}
                    {cat.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Menu Items Grid */}
      <section className="py-12 md:py-20 px-6 md:px-12 bg-[#F9F7F2]" data-testid="menu-items-section">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="h-4 bg-[#F0EBE0] rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-[#F0EBE0] rounded w-full mb-2"></div>
                  <div className="h-3 bg-[#F0EBE0] rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {activeCategory === 'All' && groupedItems ? (
                // Grouped view for All
                groupedItems.map(([category, items]) => (
                  <CategorySection 
                    key={category} 
                    category={category} 
                    items={items} 
                    icon={CATEGORY_CONFIG[category]?.icon}
                  />
                ))
              ) : (
                // Single category view
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredItems.map((item) => (
                    <MenuCard key={item.id} item={item} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Order CTA */}
      <section className="py-16 px-6 md:px-12 bg-[#4A3B32]" data-testid="order-cta">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-accent text-2xl text-[#F2CC8F] block mb-2">
            Ready to Order?
          </span>
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Just WhatsApp or Call Us!
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            No complicated ordering. Simply message us your order on WhatsApp or give us a call. 
            Pay when you pick up!
          </p>
          
          {/* Swiggy & Zomato */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="text-white/70 text-sm">Also order on:</span>
            <a 
              href="https://www.swiggy.com/menu/1242857?source=sharing" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-lg px-3 py-2 hover:scale-105 transition-transform"
            >
              <img src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/dfsl17ui_images.jfif" alt="Swiggy" className="h-6 w-6 object-contain" />
            </a>
            <a 
              href="https://zomato.onelink.me/xqzv/rcql1v1i" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-lg px-3 py-2 hover:scale-105 transition-transform"
            >
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Zomato_logo.png/100px-Zomato_logo.png" alt="Zomato" className="h-5" />
            </a>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/919611475342?text=Hi! I'd like to place an order from A1 Cafe"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="menu-whatsapp-btn"
            >
              <Button className="btn-whatsapp rounded-full px-8 py-4 text-lg font-bold">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Order on WhatsApp
              </Button>
            </a>
            <a href="tel:+919611475342" data-testid="menu-call-btn">
              <Button variant="outline" className="rounded-full px-8 py-4 text-lg font-bold border-white text-white hover:bg-white hover:text-[#4A3B32]">
                <Phone className="w-5 h-5 mr-2" />
                Call to Order
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Category Section Component - Optimized for large menus
const CategorySection = ({ category, items, icon }) => {
  return (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#E07A5F] rounded-full flex items-center justify-center text-white">
          {icon || <UtensilsCrossed className="w-5 h-5" />}
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-[#4A3B32]" style={{ fontFamily: 'Fraunces, serif' }}>
          {category}
        </h2>
        <span className="text-sm text-[#6D5D53] bg-[#F0EBE0] px-3 py-1 rounded-full">
          {items.length} items
        </span>
        <div className="flex-1 h-px bg-[#E6DCC8] ml-4"></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((item) => (
          <MenuCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
};

// Menu Card Component - Simplified for performance
const MenuCard = ({ item }) => {
  return (
    <div
      className="menu-card bg-white rounded-2xl p-5 border border-[#E6DCC8] hover:shadow-lg transition-shadow duration-200 relative"
      data-testid={`menu-item-${item.id}`}
    >
      {/* Popular Badge */}
      {item.is_popular && (
        <span className="popular-badge absolute -top-2 -right-2 px-3 py-1 rounded-full text-sm font-bold">
          Popular!
        </span>
      )}

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {/* Veg/Non-veg indicator */}
            <div 
              className={item.is_veg ? 'veg-indicator' : 'non-veg-indicator'} 
              title={item.is_veg ? 'Vegetarian' : 'Non-vegetarian'} 
            />
            <h3 className="text-lg font-bold text-[#4A3B32] truncate" style={{ fontFamily: 'Fraunces, serif' }}>
              {item.name}
            </h3>
          </div>
          <p className="text-[#6D5D53] text-sm leading-relaxed line-clamp-2">
            {item.description}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="text-2xl font-bold text-[#E07A5F]">₹{item.price}</span>
        </div>
      </div>

      {/* Order button - Links to Swiggy */}
      <a
        href="https://www.swiggy.com/menu/1242857?source=sharing"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 block"
        data-testid={`order-item-${item.id}`}
      >
        <Button className="w-full rounded-full bg-[#F0EBE0] text-[#4A3B32] hover:bg-[#4A3B32] hover:text-white transition-colors duration-200 font-medium">
          Order Now
        </Button>
      </a>
    </div>
  );
};

export default Menu;
